/**
 * Native push registration bridge.
 *
 * Owns the device-token lifecycle on top of `@capacitor/push-notifications`:
 *  - `setupPushListeners` (once): capture the token on `registration` and POST
 *    it to the API; resolve a tapped notification to what it opens and hand
 *    that to `onPushTap`'s opener.
 *  - `registerForPush` (for each session): register only when permission is
 *    already granted — which fires `registration` → token upload.
 *  - `requestPushPermission` (explicit opt-in only): surface the OS dialog,
 *    then register on grant. The only export here that can show UI.
 *
 * All native-only and best-effort: every path no-ops on web and swallows
 * errors so push never breaks login.
 */

import { Capacitor, type PermissionState } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import {
  type ActionPerformed,
  PushNotifications,
  type Token,
} from "@capacitor/push-notifications";
import {
  ANDROID_PUSH_CHANNEL_ID,
  type PushData,
  PushDataSchema,
  type PushPlatform,
} from "@vidimo/shared";
import type { RegisterPushTokenInput } from "@vidimo/shared/contracts";
import { interfaceTranslator } from "@/i18n/translate";
import { apiRequest } from "@/lib/api-request";
import { appRoutes } from "@/lib/routes";

const CAPACITOR_TO_PUSH_PLATFORM: Record<string, PushPlatform | undefined> = {
  android: "android",
  ios: "ios",
};

// Stable per-install id: it keys push_tokens rows (so rotation updates one row)
// and names the device on a sign-out, which drops its tokens.
// Generated once and persisted in Capacitor Preferences, equivalent to a
// device install id (resets on reinstall, which is fine: the orphaned token is
// pruned server-side on the next 410/UNREGISTERED). Avoids a separate native
// device-id plugin.
const DEVICE_ID_KEY = "superhost.push.device-id";

// Capacitor channel enums (the plugin exports no named constants):
// 5 = IMPORTANCE_HIGH (heads-up banner), 1 = VISIBILITY_PUBLIC (lock screen).
const ANDROID_IMPORTANCE_HIGH = 5;
const ANDROID_VISIBILITY_PUBLIC = 1;

// Chat deep-link path by chat type; guest chats live at the bare /chats.
const CHAT_PATH_BY_TYPE: Record<string, string> = {
  ai: "/chats/ai",
  staff: "/chats/staff",
};

/** A record a push can open, on top of the list it belongs to. */
export type PushRecord =
  | { reservationId: string; type: "booking" }
  | { guestId: string; reservationId: string; type: "registration" }
  | { id: string; type: "task" };

/**
 * What a tap opens. A record opens over its list, so a record the device does
 * not hold leaves the host on that list rather than on an empty screen.
 */
export type PushTap =
  | { path: string; type: "chat" }
  | { list: string; propertyId?: string; record?: PushRecord; type: "list" };

const LIST_OF_RECORD: Record<PushRecord["type"], string> = {
  booking: appRoutes.app.calendar(),
  registration: appRoutes.app.guests(),
  task: appRoutes.app.tasks(),
};

// Pushes about several records, or about none Vidimo holds. A kind missing
// here and naming no record opens nothing.
const LIST_OF_KIND: Record<string, string> = {
  channel_booking_unreadable: appRoutes.app.calendar(),
  filing_escalated: appRoutes.app.guests(),
  filing_not_checked_in: appRoutes.app.guests(),
  filing_parked: appRoutes.app.guests(),
  filing_reminder: appRoutes.app.guests(),
  reservation_cancelled: appRoutes.app.calendar(),
  reservation_created: appRoutes.app.calendar(),
  reservation_needs_room: appRoutes.app.calendar(),
};

function pushRecord(data: PushData): PushRecord | null {
  const { guestId, reservationId, taskId } = data;
  if (taskId) {
    return { id: taskId, type: "task" };
  }
  if (reservationId && guestId) {
    return { guestId, reservationId, type: "registration" };
  }
  if (reservationId) {
    return { reservationId, type: "booking" };
  }
  return null;
}

/** What a tapped notification's payload opens, or null for nothing. */
export function resolvePushTap(payload: unknown): PushTap | null {
  const parsed = PushDataSchema.safeParse(payload);
  if (!parsed.success) {
    return null;
  }
  const { data } = parsed;
  if (data.chatId) {
    // Chats open via the `?id=` param; staff/ai chats live under a path
    // prefix (see CHAT_PATH_BY_TYPE), guest chats at the bare /chats.
    const base = CHAT_PATH_BY_TYPE[data.chatType ?? ""] ?? "/chats";
    return {
      path: `${base}?id=${encodeURIComponent(data.chatId)}`,
      type: "chat",
    };
  }
  const { kind, propertyId } = data;
  const record = pushRecord(data);
  if (record) {
    return {
      list: LIST_OF_RECORD[record.type],
      propertyId,
      record,
      type: "list",
    };
  }
  const list = LIST_OF_KIND[kind];
  return list ? { list, propertyId, type: "list" } : null;
}

// A tap can land before anything can open it: a cold start from the
// notification fires it while the signed-in shell is still loading.
let tapOpener: ((tap: PushTap) => void) | null = null;
let pendingTap: PushTap | null = null;

/**
 * Open every tapped notification with `open`, starting with one that landed
 * before it was set. Returns the unsubscribe.
 */
export function onPushTap(open: (tap: PushTap) => void): () => void {
  tapOpener = open;
  if (pendingTap) {
    open(pendingTap);
    pendingTap = null;
  }
  return () => {
    if (tapOpener === open) {
      tapOpener = null;
    }
  };
}

// A token row lives as long as the session that uploaded it (push_tokens
// session_id cascade), so the device registers again whenever its session
// changes: a new sign-in, or the session a password change swaps in.
let activeSessionId: string | null = null;
let registeredSessionId: string | null = null;
let listenersAttached = false;

function pushPlatform(): PushPlatform | undefined {
  return CAPACITOR_TO_PUSH_PLATFORM[Capacitor.getPlatform()];
}

/**
 * Stable per-install id keying this device's push tokens. Shared with the Live
 * Activity token path so both kinds of token for one install resolve to
 * the same device.
 */
export async function getDeviceId(): Promise<string> {
  const existing = await Preferences.get({ key: DEVICE_ID_KEY });
  if (existing.value) {
    return existing.value;
  }
  const id = crypto.randomUUID();
  await Preferences.set({ key: DEVICE_ID_KEY, value: id });
  return id;
}

async function uploadToken(token: string): Promise<void> {
  const platform = pushPlatform();
  if (!platform) {
    return;
  }
  const deviceId = await getDeviceId();
  const body: RegisterPushTokenInput = {
    deviceId,
    platform,
    token,
  };
  await apiRequest("/push/tokens", {
    body,
    label: "Register push token",
    method: "POST",
  });
}

/** Attach the token + tap listeners once. Safe to call repeatedly. */
export async function setupPushListeners(): Promise<void> {
  if (!Capacitor.isNativePlatform() || listenersAttached) {
    return;
  }
  listenersAttached = true;

  await PushNotifications.addListener("registration", (token: Token) => {
    void uploadToken(token.value).catch((err) => {
      // Unregistered again, so the next foreground retries.
      registeredSessionId = null;
      console.warn("[push] token upload failed", err);
    });
  });

  await PushNotifications.addListener("registrationError", (err) => {
    registeredSessionId = null;
    console.warn("[push] registration error", err);
  });

  // Tap handling only: a push received while the app is foregrounded gets no
  // in-app treatment.
  await PushNotifications.addListener(
    "pushNotificationActionPerformed",
    (action: ActionPerformed) => {
      const tap = resolvePushTap(action.notification.data);
      if (!tap) {
        return;
      }
      if (tapOpener) {
        tapOpener(tap);
      } else {
        pendingTap = tap;
      }
    }
  );
}

/**
 * Create the Android channel and hand the device to the OS for a token.
 * Callers must have already confirmed permission is granted.
 */
async function registerGrantedDevice(): Promise<void> {
  // Android O+ needs the channel to exist before a channel-targeted push can
  // show. iOS has no channels — createChannel is a no-op there.
  if (Capacitor.getPlatform() === "android") {
    try {
      await PushNotifications.createChannel({
        id: ANDROID_PUSH_CHANNEL_ID,
        importance: ANDROID_IMPORTANCE_HIGH,
        name: interfaceTranslator("notifications.pushRegistration")(
          "androidChannelName"
        ),
        visibility: ANDROID_VISIBILITY_PUBLIC,
      });
    } catch (err) {
      console.warn("[push] createChannel failed", err);
    }
  }
  registeredSessionId = activeSessionId;
  await PushNotifications.register();
}

/**
 * Register for push when the OS has *already* granted permission.
 *
 * Deliberately silent — it never calls `requestPermissions()`. The app shell
 * calls this the moment a session exists, which on a fresh install is while the
 * inbox is still painting its sync skeleton; asking there spends iOS's
 * once-per-install dialog when the host has the least reason to say yes. The
 * ask belongs to `requestPushPermission` below, behind a deliberate opt-in.
 *
 * No-ops on web, if already registered for `sessionId`, or if permission is
 * anything other than `granted`.
 */
export async function registerForPush(sessionId: string): Promise<void> {
  activeSessionId = sessionId;
  if (!Capacitor.isNativePlatform() || registeredSessionId === sessionId) {
    return;
  }
  try {
    const { receive } = await PushNotifications.checkPermissions();
    if (receive !== "granted") {
      return;
    }
    await registerGrantedDevice();
  } catch (err) {
    registeredSessionId = null;
    console.warn("[push] register failed", err);
  }
}

/**
 * The only path that may surface the OS permission dialog, and only from an
 * explicit host action: the in-app primer's opt-in button, or a voice action's
 * just-in-time ask. iOS shows that dialog once per install and a denial is
 * recoverable only from the Settings app, so nothing automatic may call this.
 *
 * Registers the device when the host grants, so an opt-in reached from either
 * surface uploads a token without waiting for the next login. Resolves with the
 * resulting permission state and never throws; `denied` on web, which has no
 * runtime notification permission to request.
 */
export async function requestPushPermission(): Promise<PermissionState> {
  if (!Capacitor.isNativePlatform()) {
    return "denied";
  }
  let receive: PermissionState;
  try {
    ({ receive } = await PushNotifications.requestPermissions());
  } catch (err) {
    console.warn("[push] permission request failed", err);
    return "denied";
  }
  const isRegistered =
    registeredSessionId !== null && registeredSessionId === activeSessionId;
  if (receive === "granted" && !isRegistered) {
    // A registration failure is not a permission failure: report what the host
    // actually chose and let the next `registerForPush` retry the token.
    try {
      await registerGrantedDevice();
    } catch (err) {
      registeredSessionId = null;
      console.warn("[push] register failed", err);
    }
  }
  return receive;
}

/**
 * The OS notification permission as it stands, without asking for it. Callers
 * use this to decide whether an opt-in surface is worth showing at all.
 * `denied` on web (no runtime permission to hold) and if the plugin rejects.
 */
export async function checkPushPermission(): Promise<PermissionState> {
  if (!Capacitor.isNativePlatform()) {
    return "denied";
  }
  try {
    const { receive } = await PushNotifications.checkPermissions();
    return receive;
  } catch (err) {
    console.warn("[push] permission check failed", err);
    return "denied";
  }
}
